import React, { useMemo } from "react";
import { X, Search, MapPin, Circle, GitBranch, RotateCcw } from "lucide-react";
import {
  getStatusColor,
  getUniqueStatuses,
  getStatusDisplayName,
  formatNumber,
} from "./utils";
import { DropdownFilter } from "../../components/ui/DropdownFilter";

const Sidebar = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  searchTerm,
  setSearchTerm,
  projects,
  excavations,
}) => {
  // Get unique statuses
  const projectStatuses = useMemo(
    () => getUniqueStatuses(projects),
    [projects]
  );
  const excavationStatuses = useMemo(
    () => getUniqueStatuses(excavations),
    [excavations]
  );

  // Filter counts
  const filteredProjectsCount = useMemo(() => {
    return projects.filter((p) => {
      const matchesStatus =
        filters.projectStatus === "all" || p.status === filters.projectStatus;
      const matchesSearch =
        searchTerm === "" ||
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.pic?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch && p.coordinates;
    }).length;
  }, [projects, filters.projectStatus, searchTerm]);

  const filteredExcavationsCount = useMemo(() => {
    return excavations.filter((e) => {
      const matchesStatus =
        filters.excavationStatus === "all" ||
        e.status === filters.excavationStatus;
      const matchesSearch =
        searchTerm === "" ||
        e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.Project?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch && e.coordinates;
    }).length;
  }, [excavations, filters.excavationStatus, searchTerm]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      projectStatus: "all",
      excavationStatus: "all",
      showProjects: true,
      showExcavations: true,
      showBuffers: false,
      showConnections: false,
      bufferRadius: 500,
    });
    setSearchTerm("");
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.projectStatus !== "all" ||
      filters.excavationStatus !== "all" ||
      searchTerm !== "" ||
      !filters.showProjects ||
      !filters.showExcavations ||
      filters.showBuffers ||
      filters.showConnections ||
      filters.bufferRadius !== 500
    );
  }, [filters, searchTerm]);

  // Status counts for excavations
  const excavationStatusCounts = useMemo(() => {
    const counts = {};
    excavations.forEach((excavation) => {
      const status = excavation.status?.toLowerCase() || "inactive";
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [excavations]);

  const ToggleSwitch = ({ checked, onChange }) => (
    <button
      onClick={onChange}
      className={`relative inline-flex w-11 h-6 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
        checked ? "bg-primary" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-200 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`
        fixed lg:relative top-0 left-0 h-full w-80 bg-white shadow-xl z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        flex flex-col
      `}
      >
        {/* Close Button - Mobile Only */}
        <div className="p-4 lg:hidden">
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors ml-auto block"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            >
              <RotateCcw className="w-4 h-4" />
              Reset All Filters
            </button>
          )}

          {/* Search */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800">Search</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search projects, excavations..."
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Display Options */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="font-medium text-gray-800">Display Options</span>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Projects
                  </span>
                  <span className="text-xs text-gray-500">
                    ({formatNumber(filteredProjectsCount)})
                  </span>
                </div>
                <ToggleSwitch
                  checked={filters.showProjects}
                  onChange={() =>
                    handleFilterChange("showProjects", !filters.showProjects)
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Circle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Excavations
                  </span>
                  <span className="text-xs text-gray-500">
                    ({formatNumber(filteredExcavationsCount)})
                  </span>
                </div>
                <ToggleSwitch
                  checked={filters.showExcavations}
                  onChange={() =>
                    handleFilterChange(
                      "showExcavations",
                      !filters.showExcavations
                    )
                  }
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Circle className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-gray-700">
                      Buffer Zones
                    </span>
                  </div>
                  <ToggleSwitch
                    checked={filters.showBuffers}
                    onChange={() =>
                      handleFilterChange("showBuffers", !filters.showBuffers)
                    }
                  />
                </div>

                {/* Buffer Radius Control */}
                {filters.showBuffers && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">
                        Radius: {formatNumber(filters.bufferRadius)}m
                      </span>
                      <span className="text-gray-500">500m - 5000m</span>
                    </div>
                    <input
                      type="range"
                      min="500"
                      max="5000"
                      step="100"
                      value={filters.bufferRadius}
                      onChange={(e) =>
                        handleFilterChange(
                          "bufferRadius",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${
                          ((filters.bufferRadius - 500) / (5000 - 500)) * 100
                        }%, #d1d5db ${
                          ((filters.bufferRadius - 500) / (5000 - 500)) * 100
                        }%, #d1d5db 100%)`,
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>500m</span>
                      <span>2750m</span>
                      <span>5000m</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Connections
                  </span>
                </div>
                <ToggleSwitch
                  checked={filters.showConnections}
                  onChange={() =>
                    handleFilterChange(
                      "showConnections",
                      !filters.showConnections
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Project Filters */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-gray-800">Project Status</span>
            </div>
            <DropdownFilter
              value={filters.projectStatus}
              onChange={(val) => handleFilterChange("projectStatus", val)}
              options={[
                { value: "all", label: "All Projects" },
                ...projectStatuses.map((status) => ({
                  value: status,
                  label: getStatusDisplayName(status),
                })),
              ]}
            />
          </div>

          {/* Excavation Filters */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Circle className="w-4 h-4 text-green-600" />
              <span className="font-medium text-gray-800">
                Excavation Status
              </span>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                {formatNumber(excavations.length)}
              </span>
            </div>
            <div className="space-y-1">
              <label className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer">
                <span className="text-sm text-gray-700 font-medium">
                  All Excavations
                </span>
                <input
                  type="radio"
                  name="excavationStatus"
                  checked={filters.excavationStatus === "all"}
                  onChange={() => handleFilterChange("excavationStatus", "all")}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
              </label>

              {excavationStatuses.map((status) => (
                <label
                  key={status}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(
                        status
                      )}`}
                    >
                      {getStatusDisplayName(status)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({formatNumber(excavationStatusCounts[status] || 0)})
                    </span>
                  </div>
                  <input
                    type="radio"
                    name="excavationStatus"
                    checked={filters.excavationStatus === status}
                    onChange={() =>
                      handleFilterChange("excavationStatus", status)
                    }
                    className="w-4 h-4 text-primary focus:ring-primary"
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
